import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { Button } from './Button';
import { CHECKOUT_URL, HERO_IMAGE, HERO_IMAGE_ALT } from '../data/offer';
import { trackCheckout } from '../utils/tracking';
const negatives = ['Not a random collection of electrical videos.', 'Not a pile of old lab handouts.', 'Not a text-heavy book you have to turn into lessons.', 'Not a pile of resources you have to organize yourself.'];
const teachingFlow = ['OPEN THE SYSTEM.', 'CHOOSE THE TOPIC.', 'SHOW THE VISUAL.', 'TEACH THE CLASS.', 'RUN THE LAB.', 'CHECK THE SKILL.'];
const teachingAreas = [{
  number: '01',
  step: 'ELECTRICAL FUNDAMENTALS, CIRCUIT THEORY & ELECTRICAL SAFETY',
  detail: 'Voltage, current and resistance, Ohm’s law, series and parallel circuits, meters, PPE, lockout/tagout and test before touch.'
}, {
  number: '02',
  step: 'RESIDENTIAL ELECTRICAL WIRING, INSTALLATIONS & CIRCUIT PROTECTION',
  detail: 'Service and panels, grounding and bonding, breakers, GFCI/RCD and AFCI, receptacles, switching, lighting and testing.'
}, {
  number: '03',
  step: 'COMMERCIAL & INDUSTRIAL ELECTRICAL SYSTEMS, MOTORS & CONTROLS',
  detail: 'Three-phase wye and delta, transformers, distribution, motors, contactors, relays and start/stop and reversing control.'
}, {
  number: '04',
  step: 'ELECTRICAL DIAGRAMS, TESTING, TROUBLESHOOTING & FAULT DIAGNOSIS',
  detail: 'Schematics, ladder and one-line diagrams, meter technique, insulation testing and a systematic fault-finding method.'
}, {
  number: '05',
  step: 'INDUSTRIAL AUTOMATION, PLCs, VARIABLE FREQUENCY DRIVES & SMART ELECTRICAL SYSTEMS',
  detail: 'PLC hardware and I/O, ladder logic, timers, sensors, analog signals, VFD setup and faults, and smart building systems.'
}, {
  number: '06',
  step: 'ELECTRICAL INSTRUCTION, WORKSHOP TRAINING & PRACTICAL ASSESSMENTS',
  detail: 'Lesson plans, demonstrations, lab safety sign-offs, bench coaching, circuit-building activities, rubrics and a course calendar.'
}];
const ease = [0.23, 1, 0.32, 1] as const;
export function SolutionReveal() {
  return <section className="py-20 px-4 bg-[#F3F6FC] relative overflow-hidden" data-id="element-282" data-mp="2qk2d3b9lcy3m">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-1 bg-[#F5C400]" data-id="element-283" data-mp="2mhxlei2pebjm" />

      <div className="max-w-4xl mx-auto text-center" data-id="element-284" data-mp="1ysd21y2n1c9t">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease
      }} data-id="element-285" data-mp="1yt62khb075ji">
          <p className="text-sm md:text-base font-semibold text-[#7A5C00] mb-4" data-id="element-286" data-mp="27umb0ji9cxl3">
            The Complete Visual Electrical Training System for Electrical Instructors, Technical Schools &amp; Training Workshops
          </p>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 text-[#0B1F4D]" data-id="element-287" data-mp="3jaaxl9rlnuld">
            ELECTRICAL TECHNOLOGY INSTRUCTOR <span className="text-[#1236C0]" data-id="element-288" data-mp="3mqdv9fu859wk">ATLAS</span>
          </h2>
          <p className="text-base md:text-lg text-[#0B1F4D] font-semibold mb-6" data-id="element-289" data-mp="3msi66hqjj96c">
            A Complete 6-IN-1 Visual Electrical Systems &amp; Technical Training System
          </p>
          <img src={HERO_IMAGE} alt={HERO_IMAGE_ALT} className="w-full max-w-sm h-auto mx-auto mb-6 drop-shadow-xl" loading="lazy" data-id="element-290" data-mp="3ctsksezfr6lc" />
          <p className="text-base md:text-lg text-[#7A5C00] font-semibold mb-10" data-id="element-291" data-mp="9br8ntmxr9pd">
            6 PROFESSIONAL VOLUMES · 300 VISUAL TRAINING PAGES · 5 EXCLUSIVE BONUSES
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 text-left" data-id="element-292" data-mp="3vqk24ivf3bmr">
          <motion.div initial={{
          opacity: 0,
          x: -12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="space-y-3 bg-white/60 p-6 rounded-2xl border border-[#D2DCEC]" data-id="element-293" data-mp="4jr0xf0mjci8">
            {negatives.map(statement => <div key={statement} className="flex items-start gap-3" data-id="element-294" data-mp="34iadv26gpzqe">
                <X className="text-[#0B1F4D]/50 shrink-0 mt-1" size={18} data-id="element-295" data-mp="30i0azydyvpfr" />
                <p className="text-slate-700/80 text-sm" data-id="element-296" data-mp="2vix2u52ox33k">{statement}</p>
              </div>)}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="bg-white p-6 rounded-2xl border-2 border-[#0B1F4D]" data-id="element-297" data-mp="2qlx5cijwp8o2">
            <div className="flex items-start gap-3" data-id="element-298" data-mp="3ehj7tv1bvecr">
              <Check className="text-[#1236C0] shrink-0 mt-1" size={20} data-id="element-299" data-mp="39jhnl6s618zp" />
              <div className="space-y-3" data-id="element-300" data-mp="39j7xzkb193uj">
                <p className="text-[#0B1F4D] font-medium text-base leading-relaxed" data-id="element-301" data-mp="atzmpbqvrv5d">
                  ELECTRICAL TECHNOLOGY INSTRUCTOR ATLAS is a complete visual training system for electrical instructors,
                  technical schools, vocational programs and training workshops — from beginner to
                  advanced.
                </p>
                <p className="text-[#7A5C00] font-semibold text-sm leading-relaxed" data-id="element-302" data-mp="id8vb2rkbptd">
                  Every page gives you an illustration, labeled diagram or wiring sequence, a step-by-step
                  breakdown, a ❌ common mistake vs. ✅ correct method comparison, a teaching cue, a drill,
                  a quick check and an Easy / Standard / Advanced variant — all in one place.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-10" data-id="element-303" data-mp="163385kbvtedm">
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5" data-id="element-304" data-mp="164xjq819ju51">
            {teachingFlow.map(line => <li key={line} className="text-lg md:text-xl font-extrabold tracking-wide text-[#0B1F4D]" data-id="element-305" data-mp="1g1k2mra14b4f">
                {line}
              </li>)}
          </ul>
          <p className="mt-8 text-lg md:text-xl font-bold text-[#0B1F4D]" data-id="element-306" data-mp="3h2qm3hcjm122">
            Make Your Next Electrical Class Easier to Prepare and More Visual to Teach.
          </p>
          <div className="flex justify-center mt-5" data-id="element-307" data-mp="3c1sw4ilak2q0">
            <Button href={CHECKOUT_URL} onLinkClick={trackCheckout} className="text-sm sm:text-lg py-3 sm:py-4 px-5 sm:px-8" data-id="element-308" data-mp="3pwbzoxcz1c9w">
              GET MY COMPLETE 6-IN-1 ELECTRICAL TRAINING SYSTEM NOW!
            </Button>
          </div>
        </div>

        <div className="mt-12" data-id="element-309" data-mp="3utbwehnxl2oc">
          <p className="text-[#7A5C00] text-sm font-semibold uppercase tracking-widest mb-5" data-id="element-310" data-mp="3uu52dgf2fo5f">
            The System Covers Six Essential Electrical Teaching Areas
          </p>
          <ol className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-id="element-311" data-mp="8ohyyossi2ha">
            {teachingAreas.map(item => <li key={item.step} className="flex flex-col items-center px-3 py-4 bg-white rounded-xl border border-[#D2DCEC]" data-id="element-312" data-mp="2ei7xj3w8chtr">
                <span className="text-sm font-bold text-[#7A5C00] mb-1" data-id="element-313" data-mp="20b4xmvsbmk3b">{item.number}</span>
                <span className="text-lg md:text-xl font-extrabold tracking-tight leading-tight text-[#0B1F4D]" data-id="element-314" data-mp="24awyembh3ly4">
                  {item.step}
                </span>
                <span className="text-sm text-slate-700/80 mt-1" data-id="element-315" data-mp="1vjd3ycdvby8y">{item.detail}</span>
              </li>)}
          </ol>
          <p className="mt-8 text-sm md:text-base font-bold tracking-widest text-[#0B1F4D]" data-id="element-316" data-mp="1gluibiwhm15q">
            SIX AREAS. SIX COMPLETE VOLUMES. ONE PROFESSIONAL TRAINING SYSTEM.
          </p>
        </div>
      </div>
    </section>;
}